import { PageLoadError } from '@components/page-load-error';
import {
  getAccountsQueryOptions,
  getProductsQueryOptions,
} from '@features/accounts/api';
import {
  getSavingsUnavailableReason,
  SavingsUnavailableReason,
} from '@features/accounts/get-savings-unavailable-reason';
import { filterAccountsByType } from '@features/accounts/filterAccountsByType';
import { Account, Product } from '@features/accounts/types';
import { getProfileQueryOptions } from '@features/user/api/get-profile';
import { SavingsProductStatus } from '@features/user/types/common-application-eligibility';
import { createFileRoute } from '@tanstack/react-router';
import AddAccountsLayout from './-layouts/add-accounts-layout';

type AddAccountsLoaderData = {
  savingsProducts: Product[];
  savingsAccounts: Account[];
  canApply: boolean;
  unavailableReason: SavingsUnavailableReason | null;
};

export const Route = createFileRoute('/_authenticated/transfers/add-accounts')({
  component: AddAccountsLayout,
  errorComponent: ({ error }) => (
    <PageLoadError pageName='Add Accounts' error={error} />
  ),
  loader: async ({ context }): Promise<AddAccountsLoaderData> => {
    const products =
      context.queryClient.getQueryData(getProductsQueryOptions.queryKey) ??
      (await context.queryClient.fetchQuery(getProductsQueryOptions));

    const accounts =
      context.queryClient.getQueryData(getAccountsQueryOptions.queryKey) ??
      (await context.queryClient.fetchQuery(getAccountsQueryOptions));

    const profile =
      context.queryClient.getQueryData(getProfileQueryOptions.queryKey) ??
      (await context.queryClient.fetchQuery(getProfileQueryOptions));

    const eligibility = profile.commonApplicationEligibility;
    const available = new Set(
      eligibility.savingsProducts
        .filter(({ status }) => status === SavingsProductStatus.AVAILABLE)
        .map(({ uuid }) => uuid.toLowerCase()),
    );
    const { savingsAccounts } = filterAccountsByType(accounts?.visible);

    return {
      savingsProducts: products.savings.filter((product) =>
        available.has(product.uuid.toLowerCase()),
      ),
      savingsAccounts,
      canApply: eligibility.canApply,
      unavailableReason: getSavingsUnavailableReason(eligibility),
    };
  },
});
